'use client';

import React from 'react';
import { BarChart, Bar, XAxis, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';

export interface PulseSegmentDef {
  /** CSS colour string. */
  color: string;
  value: number;
}

export interface PulseBucket {
  /** X-axis label (e.g. "06:00"). */
  label: string;
  segments: PulseSegmentDef[];
}

export interface SchedulePulseProps {
  buckets: PulseBucket[];
  /** Chart area height in px. Default 64. */
  height?: number;
  /** Show x-axis bucket labels. Default true. */
  showLabels?: boolean;
  className?: string;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

function transformBuckets(buckets: PulseBucket[]): {
  data: Record<string, string | number>[];
  seriesCount: number;
  colors: string[];
} {
  if (buckets.length === 0) return { data: [], seriesCount: 0, colors: [] };

  const seriesCount = Math.max(...buckets.map((b) => b.segments.length));
  const colors = Array.from(
    { length: seriesCount },
    (_, i) => buckets.find((b) => b.segments[i])?.segments[i].color ?? 'currentColor',
  );
  const data = buckets.map((b) => {
    const row: Record<string, string | number> = { label: b.label };
    for (let i = 0; i < seriesCount; i++) {
      row[`s${i}`] = b.segments[i]?.value ?? 0;
    }
    return row;
  });

  return { data, seriesCount, colors };
}

/**
 * Stacked bar histogram for schedule demand density. Each bucket is a
 * time window; segments within a bucket are stacked by activity type.
 *
 * Segment colors should come from `theme.colors.activity.*` to stay on-token.
 *
 * @example
 * <SchedulePulse
 *   buckets={[
 *     { label: '06:00', segments: [
 *       { color: theme.colors.activity.pickup.bar, value: 3 },
 *       { color: theme.colors.activity.dropoff.bar, value: 2 },
 *     ]},
 *   ]}
 * />
 */
export function SchedulePulse({
  buckets,
  height = 64,
  showLabels = true,
  className,
}: SchedulePulseProps) {
  const { data, seriesCount, colors } = transformBuckets(buckets);
  const n = buckets.length;

  const ariaLabel =
    n === 0 ? 'Schedule histogram: no data' : `Schedule histogram: ${n} time buckets`;

  const totalHeight = showLabels ? height + 20 : height;

  return (
    <Wrapper className={className}>
      <div role="img" aria-label={ariaLabel} style={{ width: '100%', height: totalHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 2, right: 2, left: 2, bottom: 0 }}
            barCategoryGap="10%"
          >
            {showLabels && (
              <XAxis
                dataKey="label"
                tick={{ fontSize: 8, fill: 'currentColor', opacity: 0.5 }}
                axisLine={false}
                tickLine={false}
                height={20}
              />
            )}
            {Array.from({ length: seriesCount }, (_, i) => (
              <Bar
                key={i}
                dataKey={`s${i}`}
                stackId="a"
                fill={colors[i]}
                isAnimationActive={false}
                radius={i === seriesCount - 1 ? [1, 1, 0, 0] : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Wrapper>
  );
}
